import {useState, useEffect, useLayoutEffect, useRef} from "react";
import {useVirtualizer} from "@tanstack/react-virtual";
import {SearchX} from "lucide-react";
import VideoModal from "../components/VideoModal";
import VideoCard from "../components/VideoCard";

// Match the Tailwind breakpoints the grid used before virtualization:
// 1 column < md(768), 2 columns < lg(1024), 3 columns at lg+.
const columnsForWidth = (w) => (w >= 1024 ? 3 : w >= 768 ? 2 : 1);

const useColumns = () => {
  const [cols, setCols] = useState(() =>
    typeof window === "undefined" ? 1 : columnsForWidth(window.innerWidth),
  );
  useEffect(() => {
    const onResize = () => setCols(columnsForWidth(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return cols;
};

const VideoGrid = ({
  videos,
  recentVideos,
  watchHistory,
  handleVideoProgress,
  updateDailyStat,
  scrollRef,
}) => {
  const [expandedVideo, setExpandedVideo] = useState(null);

  const cols = useColumns();
  const listRef = useRef(null);
  const rowCount = Math.ceil(videos.length / cols);

  // The grid sits below the stats/sort/filter sections inside the dashboard's
  // own scroll container, so the virtualizer needs the grid's offset within
  // that container as its scrollMargin. listRef.offsetTop is relative to the
  // nearest positioned ancestor, which is the scroll container (position
  // relative), so it is exactly that offset.
  const [scrollMargin, setScrollMargin] = useState(0);
  useLayoutEffect(() => {
    if (listRef.current) setScrollMargin(listRef.current.offsetTop);
  }, [cols, videos.length]);

  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef?.current ?? null,
    estimateSize: () => 360,
    overscan: 4,
    scrollMargin,
  });

  // Recycle measurements when the column count flips (card width, and thus
  // height, changes), otherwise rows keep their stale measured heights.
  useEffect(() => {
    rowVirtualizer.measure();
  }, [cols, rowVirtualizer]);

  const colsClass =
    cols === 3 ? "grid-cols-3" : cols === 2 ? "grid-cols-2" : "grid-cols-1";

  const renderCard = (v) => (
    <VideoCard
      key={v.id}
      video={v}
      savedTime={watchHistory[v.id]?.lastPosition || 0}
      onInteraction={() => updateDailyStat("clicks")}
      onExpand={() => setExpandedVideo(v)}
    />
  );

  // Virtualize only when we have a real scroll container to measure against.
  // Without one (e.g. jsdom in unit tests, which has no layout), fall back to
  // rendering every card in a plain responsive grid so the grid still works.
  const virtualize = !!scrollRef;

  return (
    <>
      {expandedVideo && (
        <VideoModal
          video={expandedVideo}
          watchHistory={watchHistory}
          onClose={() => setExpandedVideo(null)}
          recentVideos={recentVideos}
          onProgress={handleVideoProgress}
          // No inline grid players remain, so there's no "currently playing"
          // id to track -- the modal is the only player. A no-op keeps the
          // modal's onPlay contract satisfied.
          onPlay={() => {}}
          onSwitchVideo={(nextVideo) => {
            // Clicking a suggestion swaps the open modal to the suggested
            // video.
            setExpandedVideo(nextVideo);
          }}
        />
      )}

      {videos.length > 0 && virtualize && (
        <div
          ref={listRef}
          style={{height: rowVirtualizer.getTotalSize(), position: "relative"}}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const start = virtualRow.index * cols;
            const rowVideos = videos.slice(start, start + cols);
            return (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                }}
              >
                <div className={`grid ${colsClass} gap-x-5 gap-y-2 pb-4`}>
                  {rowVideos.map(renderCard)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {videos.length > 0 && !virtualize && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-2">
          {videos.map(renderCard)}
        </div>
      )}

      {/* Round-4: when the active search / category filter matches no
          videos the grid used to render empty with no explanation, so
          it looked broken. Show an explicit empty state instead. */}
      {videos.length === 0 && (
        <div
          role="status"
          className="flex flex-col items-center justify-center text-center py-16 px-6 rounded-[24px] border border-dashed border-slate-200 bg-slate-50/60"
        >
          <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 mb-4">
            <SearchX size={26} />
          </div>
          <p className="text-base font-semibold text-slate-700">
            No videos found
          </p>
          <p className="text-sm text-slate-400 mt-1">
            Try a different search term or category.
          </p>
        </div>
      )}
    </>
  );
};

export default VideoGrid;
